/**
 * Discovery API Client — GE023 « Découverte locale »
 * Endpoint: /api/discovery/
 *
 * Contrats API :
 *  - GET events/ (paginé 20) : {results: [...], count, next, previous}
 *  - GET events/:id/
 *  - POST events/:id/interactions/ {type, context?}
 *  - POST/DELETE events/:id/calendar/
 *  - GET events/:id/share/
 *  - GET/PATCH preferences/
 *  - GET categories/
 *  - GET changes/ (?all=1 optionnel)
 *  - POST changes/:id/resolve/ {action: "update"|"ignore"}
 */

import {
  makeGetRequest,
  makeGetAllRequest,
  makePostRequest,
  makePatchRequest,
  makeDeleteRequest,
} from './api'

const BASE = '/api/discovery'

// ─────────────────────── Events ───────────────────────

/**
 * Récupère mes événements recommandés/sauvegardés (paginé, makeGetAllRequest pour tout charger).
 * @param {Object} params - {saved: 1, upcoming: 0, ...}
 * @returns {Promise} {status, data: {results: [...], count, ...}}
 */
export async function getDiscoveryEvents(params = {}) {
  let url = `${BASE}/events/`
  const query = new URLSearchParams()
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null) query.append(k, String(v))
  })
  if (query.toString()) {
    url += `?${query.toString()}`
  }
  return makeGetAllRequest(url, {}, true)
}

/**
 * Récupère un événement en détail (liste et détail utilisent le même endpoint).
 * @param {number} eventId
 * @returns {Promise} {status, data: {id, canonical_name, description, ...}}
 */
export function getDiscoveryEventDetail(eventId) {
  return makeGetRequest(`${BASE}/events/${eventId}/`, {}, true)
}

/**
 * Trace une interaction utilisateur (OPEN, SAVE, UNSAVE, INTERESTED, NOT_INTERESTED, etc.).
 * @param {number} eventId
 * @param {string} type - OPEN | SAVE | UNSAVE | INTERESTED | NOT_INTERESTED | BOOKING_CLICK | CATEGORY_MORE | CATEGORY_LESS | ORGANIZER_BLOCK | SHARE
 * @param {Object} context - optionnel, contexte libre
 * @returns {Promise} {status, data: {status: "ok", type}}
 */
export function recordEventInteraction(eventId, type, context = null) {
  const body = { type }
  if (context) body.context = context
  return makePostRequest(`${BASE}/events/${eventId}/interactions/`, body, {}, true)
}

/**
 * Ajoute un événement à l'agenda Life.
 * @param {number} eventId
 * @returns {Promise} {status, data: {calendar_event_id, created}}
 */
export function addEventToCalendar(eventId) {
  return makePostRequest(`${BASE}/events/${eventId}/calendar/`, {}, {}, true)
}

/**
 * Retire un événement de l'agenda Life.
 * @param {number} eventId
 * @returns {Promise} {status: 204}
 */
export function removeEventFromCalendar(eventId) {
  return makeDeleteRequest(`${BASE}/events/${eventId}/calendar/`, {}, true)
}

/**
 * Récupère l'URL de partage d'un événement (trace aussi l'interaction SHARE).
 * @param {number} eventId
 * @returns {Promise} {status, data: {url, title, text, official_url}}
 */
export function getShareUrl(eventId) {
  return makeGetRequest(`${BASE}/events/${eventId}/share/`, {}, true)
}

// ─────────────────────── Preferences ───────────────────────

/**
 * Récupère mes préférences de découverte (get_or_create implicite côté backend).
 * @returns {Promise} {status, data: {enabled, location_mode, preferred_categories, ...}}
 */
export function getDiscoveryPreferences() {
  return makeGetRequest(`${BASE}/preferences/`, {}, true)
}

/**
 * Sauvegarde mes préférences de découverte (PATCH).
 * @param {Object} data - champs à mettre à jour
 * @returns {Promise} {status, data: {...}}
 */
export function patchDiscoveryPreferences(data) {
  return makePatchRequest(`${BASE}/preferences/`, data, {}, true)
}

// ─────────────────────── Categories ───────────────────────

/**
 * Récupère la taxonomie des catégories actives (parent-child).
 * @returns {Promise} {status, data: [{code, label, parent_code?}, ...]}
 */
export function getDiscoveryCategories() {
  return makeGetRequest(`${BASE}/categories/`, {}, true)
}

// ─────────────────────── Changes ───────────────────────

/**
 * Récupère les écarts détectés sur mes événements en agenda.
 * @param {Object} params - {all: 1 pour voir tous, sinon PENDING uniquement}
 * @returns {Promise} {status, data: {results: [...], count, ...}}
 */
export async function getEventChanges(params = {}) {
  let url = `${BASE}/changes/`
  const query = new URLSearchParams()
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null) query.append(k, String(v))
  })
  if (query.toString()) {
    url += `?${query.toString()}`
  }
  return makeGetAllRequest(url, {}, true)
}

/**
 * Résout un écart détecté (mise à jour agenda ou ignoré).
 * @param {number} changeId
 * @param {string} action - "update" ou "ignore"
 * @returns {Promise} {status, data: EventChangeLogSerializer}
 */
export function resolveEventChange(changeId, action) {
  return makePostRequest(`${BASE}/changes/${changeId}/resolve/`, { action }, {}, true)
}
